/**
 * Right-side Pop-up Properties panel.
 *
 * Same editing model and the same panel chrome as Symbology and Label
 * Properties — draft / session / committed — so the three behave identically
 * and an admin learns one panel, not three. The configurations stay
 * independent: nothing here reads or writes `layer.renderer` or `layer.labeling`.
 *
 * A pop-up cannot be previewed on the map (it only appears on click), so the
 * preview is IN the panel and renders through the real popup card and the real
 * renderer. What the preview shows is what a click will show.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, GripVertical, Search, X } from 'lucide-react';
import type { AppModule, Layer } from '../types';
import { getLayerDisplayName } from '../layers/capabilities';
import { PropertySection, ToggleField } from '../components/layers/symbology/SymbolProperties';
import FeatureAttributePopup from '../components/FeatureAttributePopup';
import { extractMetadata } from '../utils/featureFormat';
import { fetchLabelFields, type LabelField } from '../labeling/LabelService';
import { fetchSampleAttributes } from './PopupService';
import { resolvePopupFields, resolvePopupTitle } from './PopupRenderer';
import {
  createPopup,
  mergePopupFields,
  normalisePopup,
  popupFieldsBlock,
  type LayerPopup,
  type PopupFieldConfig,
  type PopupFieldFormat,
} from './PopupTypes';

interface Props {
  layer: Layer | null;
  module: AppModule;
  open: boolean;
  onApply: (popup: LayerPopup) => void;
  onClose: () => void;
}

export default function PopupProperties({ layer, module, open, onApply, onClose }: Props) {
  /* Committed value for this session, so Cancel has something to go back to
     after an Apply. Mirrors the labelling panel exactly. */
  const [sessionPopup, setSessionPopup] = useState<LayerPopup>(() => createPopup());
  const [draft, setDraft] = useState<LayerPopup>(() => createPopup());
  const [dirty, setDirty] = useState(false);
  const [fields, setFields] = useState<LabelField[]>([]);
  const [sample, setSample] = useState<Record<string, unknown>>({});
  const [expandedField, setExpandedField] = useState<string | null>(null);
  const [loadingSchema, setLoadingSchema] = useState(false);
  /** Filters the field list only — never the configuration itself. */
  const [fieldQuery, setFieldQuery] = useState('');
  /** Index of the row being dragged, or null. */
  const [dragIndex, setDragIndex] = useState<number | null>(null);

  /* Re-seed whenever a different layer is opened. */
  useEffect(() => {
    if (!open || !layer) return;
    const stored = layer.popup ? normalisePopup(layer.popup) : createPopup();
    setSessionPopup(stored);
    setDraft(stored);
    setDirty(false);
    setExpandedField(null);
    setFieldQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, layer?.id]);

  /* Schema + one real feature, from whichever service backs this layer. */
  useEffect(() => {
    if (!open || !layer) return;
    let cancelled = false;
    setLoadingSchema(true);

    void Promise.all([fetchLabelFields(layer, module), fetchSampleAttributes(layer, module)]).then(
      ([schema, attributes]) => {
        if (cancelled) return;
        setFields(schema);
        setSample(attributes);
        setLoadingSchema(false);

        /* Fold newly discovered columns into the draft so the list shows the
           layer's real schema. A brand-new configuration starts with every
           field ticked, which reproduces today's automatic pop-up. */
        const discovered = schema.length > 0 ? schema.map((entry) => entry.name) : Object.keys(attributes);
        if (discovered.length === 0) return;

        setDraft((current) => {
          const block = popupFieldsBlock(current);
          const merged = mergePopupFields(block, discovered);
          if (merged.length === block.fields.length) return current;
          return { ...current, blocks: [{ ...block, fields: merged }] };
        });
      },
    );

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, layer?.id, module.arcgisUrl, module.geoServerUrl]);

  const block = popupFieldsBlock(draft);

  /** Service aliases, so an untouched field still shows its published name. */
  const aliases = useMemo(() => {
    const map: Record<string, string> = {};
    for (const field of fields) map[field.name] = field.alias;
    return map;
  }, [fields]);

  const update = (changes: Partial<LayerPopup>) => {
    setDraft((current) => ({ ...current, ...changes }));
    setDirty(true);
  };

  const updateFields = (next: PopupFieldConfig[]) => {
    setDraft((current) => ({ ...current, blocks: [{ ...popupFieldsBlock(current), fields: next }] }));
    setDirty(true);
  };

  const updateField = (field: string, changes: Partial<PopupFieldConfig>) =>
    updateFields(block.fields.map((entry) => (entry.field === field ? { ...entry, ...changes } : entry)));

  const updateFormat = (field: string, changes: Partial<PopupFieldFormat>) => {
    const current = block.fields.find((entry) => entry.field === field)?.format ?? {};
    const next: PopupFieldFormat = { ...current, ...changes };
    // Drop keys that are back to "automatic" so the saved config stays small.
    for (const key of Object.keys(next) as (keyof PopupFieldFormat)[]) {
      const value = next[key];
      if (value === undefined || value === '') delete next[key];
    }
    updateField(field, { format: Object.keys(next).length > 0 ? next : undefined });
  };

  /** Order in this list IS the order in the pop-up. */
  const moveField = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= block.fields.length) return;
    const next = [...block.fields];
    [next[index], next[target]] = [next[target], next[index]];
    updateFields(next);
  };

  /** Drag-and-drop reorder. Lifts the row out and re-inserts it at the target. */
  const dropField = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    const next = [...block.fields];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    updateFields(next);
  };

  const setAllVisible = (visible: boolean) =>
    updateFields(block.fields.map((entry) => ({ ...entry, visible })));

  const cancel = useCallback(() => {
    setDraft(sessionPopup);
    setDirty(false);
    onClose();
  }, [onClose, sessionPopup]);

  const apply = () => {
    onApply(draft);
    setSessionPopup(draft);
    setDirty(false);
  };

  /** Back to "show everything", which is what a layer with no pop-up does. */
  const reset = () => {
    const fresh = createPopup(block.fields.map((entry) => entry.field));
    setDraft(fresh);
    setDirty(true);
    setExpandedField(null);
  };

  // Escape cancels, matching the other two panels.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancel();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, cancel]);

  /* The preview runs the SAME renderer the map runs, over one real feature. */
  const preview = useMemo(() => {
    const { metadata, remaining } = extractMetadata(sample);
    const fallbackTitle = layer ? getLayerDisplayName(layer) : 'Layer';
    return {
      title: resolvePopupTitle(draft.title, sample, fallbackTitle),
      rows: resolvePopupFields(remaining, aliases, draft),
      metadata,
    };
  }, [sample, draft, aliases, layer]);

  const visibleCount = block.fields.filter((entry) => entry.visible).length;

  /* Rows carry their REAL index, so reordering stays correct while filtered —
     the search narrows what is shown, never what is configured. */
  const needle = fieldQuery.trim().toLowerCase();
  const rows = block.fields
    .map((config, index) => ({ config, index }))
    .filter(({ config }) => {
      if (!needle) return true;
      const label = config.label ?? aliases[config.field] ?? '';
      return (
        config.field.toLowerCase().includes(needle) || label.toLowerCase().includes(needle)
      );
    });
  const filtering = needle !== '';

  return (
    <aside
      role="dialog"
      aria-label="Pop-up properties"
      aria-hidden={!open}
      /* Same geometry as Symbology and Label Properties: a right-hand panel on
         desktop, a bottom sheet measured against the viewport on phones. */
      className={`gl-card gl-surface--strong gl-surface--over-map z-[4000] flex flex-col overflow-hidden transition-transform duration-300 ease-out
        fixed inset-x-2 bottom-2 h-[72%]
        sm:absolute sm:inset-x-auto sm:bottom-auto sm:right-2 sm:top-2 sm:h-[calc(100%-1rem)] sm:w-[340px] ${
          open
            ? 'translate-y-0 sm:translate-x-0'
            : 'pointer-events-none invisible translate-y-[calc(100%+1rem)] sm:translate-y-0 sm:translate-x-[calc(100%+1rem)]'
        }`}
      style={{
        borderRadius: 'var(--glass-radius-lg)',
        background: 'var(--glass-bg-strong)',
        backdropFilter: open ? 'blur(var(--glass-blur)) saturate(var(--glass-saturate))' : undefined,
        WebkitBackdropFilter: open
          ? 'blur(var(--glass-blur)) saturate(var(--glass-saturate))'
          : undefined,
        border: '1px solid var(--glass-border)',
        boxShadow: 'var(--glass-shadow-lg)',
      }}
    >
      <header className="gl-header flex flex-shrink-0 items-start justify-between px-4 py-3">
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-[#0F2942]">Pop-up Properties</h3>
          <p className="truncate text-xs text-slate-500" title={layer ? getLayerDisplayName(layer) : ''}>
            {layer ? getLayerDisplayName(layer) : '—'}
          </p>
        </div>
        <button
          type="button"
          onClick={cancel}
          aria-label="Close pop-up properties"
          className="rounded p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {!layer ? null : (
          <>
            {/* ── POP-UP ── */}
            <PropertySection title="Pop-up">
              <ToggleField
                label="Enable pop-up"
                checked={draft.enabled}
                onChange={(enabled) => update({ enabled })}
              />
              <p className="mt-1 text-[10px] leading-snug text-slate-400">
                {draft.enabled
                  ? 'Clicking a feature in this layer opens the pop-up below.'
                  : 'This layer is skipped when the map is clicked. Other layers still respond.'}
              </p>
            </PropertySection>

            {/* ── TITLE ── */}
            <PropertySection title="Title" collapsible>
              <input
                value={draft.title}
                onChange={(event) => update({ title: event.target.value })}
                placeholder={getLayerDisplayName(layer)}
                aria-label="Pop-up title"
                className="w-full rounded-lg border border-slate-200 px-2 py-1.5 text-xs text-slate-800 outline-none focus:border-blue-300 focus:ring-2 focus:ring-blue-100"
              />
              <p className="mt-1 text-[10px] leading-snug text-slate-400">
                Use <code className="rounded bg-slate-100 px-1">{'{field}'}</code> to insert a value,
                e.g. <code className="rounded bg-slate-100 px-1">{'{name} — {diameter} mm'}</code>.
                Leave empty to use the layer name.
              </p>
              {fields.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {fields.slice(0, 12).map((field) => (
                    <button
                      key={field.name}
                      type="button"
                      onClick={() => update({ title: `${draft.title}{${field.name}}` })}
                      title={`Insert {${field.name}}`}
                      className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-slate-600 transition hover:border-blue-300 hover:text-blue-700"
                    >
                      {field.name}
                    </button>
                  ))}
                </div>
              )}
            </PropertySection>

            {/* ── FIELDS ── */}
            <PropertySection
              title={`Fields (${visibleCount}/${block.fields.length})`}
              collapsible
              trailing={
                block.fields.length > 0 ? (
                  <div className="flex gap-1">
                    <button
                      type="button"
                      onClick={() => setAllVisible(true)}
                      className="rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 transition hover:bg-slate-50"
                    >
                      All
                    </button>
                    <button
                      type="button"
                      onClick={() => setAllVisible(false)}
                      className="rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-medium text-slate-500 transition hover:bg-slate-50"
                    >
                      None
                    </button>
                  </div>
                ) : undefined
              }
            >
              {loadingSchema && block.fields.length === 0 ? (
                <p className="py-2 text-[11px] text-slate-400">Reading this layer’s fields…</p>
              ) : block.fields.length === 0 ? (
                <p className="py-2 text-[11px] leading-snug text-slate-400">
                  This layer did not report its fields, so the pop-up will show every attribute it
                  returns. Add a GeoServer or ArcGIS URL that answers a schema request to configure
                  them individually.
                </p>
              ) : (
                <>
                {/* Long schemas are common — a gas layer can publish sixty
                    columns — so the list needs a way in that is not scrolling. */}
                {block.fields.length > 8 && (
                  <div className="relative mb-1.5">
                    <Search className="pointer-events-none absolute left-2 top-1/2 h-3 w-3 -translate-y-1/2 text-slate-400" />
                    <input
                      value={fieldQuery}
                      onChange={(event) => setFieldQuery(event.target.value)}
                      placeholder="Find a field..."
                      aria-label="Find a field"
                      className="w-full rounded-lg border border-slate-200 py-1 pl-7 pr-2 text-[11px] text-slate-800 outline-none focus:border-blue-300"
                    />
                  </div>
                )}

                <div className="divide-y divide-slate-100 rounded-lg border border-slate-200 bg-white">
                  {rows.length === 0 && (
                    <p className="px-2 py-3 text-center text-[11px] text-slate-400">
                      No field matches that search.
                    </p>
                  )}
                  {rows.map(({ config, index }) => {
                    const expanded = expandedField === config.field;
                    const alias = aliases[config.field];
                    return (
                      <div
                        key={config.field}
                        /* Reordering is disabled while filtering: dragging a row
                           past neighbours that are hidden would land it
                           somewhere the user cannot see. */
                        draggable={!filtering}
                        onDragStart={() => setDragIndex(index)}
                        onDragEnd={() => setDragIndex(null)}
                        onDragOver={(event) => {
                          if (filtering || dragIndex === null) return;
                          event.preventDefault();
                        }}
                        onDrop={(event) => {
                          if (filtering || dragIndex === null) return;
                          event.preventDefault();
                          dropField(dragIndex, index);
                          setDragIndex(null);
                        }}
                        className={`px-1.5 py-1 transition ${
                          dragIndex === index ? 'bg-blue-50 opacity-60' : ''
                        }`}
                      >
                        <div className="flex items-center gap-1.5">
                          {!filtering && (
                            <GripVertical
                              aria-hidden="true"
                              className="h-3 w-3 flex-shrink-0 cursor-grab text-slate-300"
                            />
                          )}
                          <input
                            type="checkbox"
                            checked={config.visible}
                            onChange={() => updateField(config.field, { visible: !config.visible })}
                            aria-label={`Show ${config.field}`}
                            className="h-3.5 w-3.5 flex-shrink-0 accent-blue-600"
                          />
                          <button
                            type="button"
                            onClick={() => setExpandedField(expanded ? null : config.field)}
                            aria-expanded={expanded}
                            className="flex min-w-0 flex-1 items-center gap-1 text-left"
                          >
                            {expanded ? (
                              <ChevronDown className="h-3 w-3 flex-shrink-0 text-slate-400" />
                            ) : (
                              <ChevronRight className="h-3 w-3 flex-shrink-0 text-slate-400" />
                            )}
                            <span className="min-w-0 flex-1 truncate text-[11px] text-slate-700">
                              {config.label?.trim() || alias || config.field}
                            </span>
                          </button>
                          {/* Kept beside the drag handle: dragging is not
                              reachable from a keyboard. */}
                          <button
                            type="button"
                            onClick={() => moveField(index, -1)}
                            disabled={filtering || index === 0}
                            aria-label={`Move ${config.field} up`}
                            className="rounded p-0.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            type="button"
                            onClick={() => moveField(index, 1)}
                            disabled={filtering || index === block.fields.length - 1}
                            aria-label={`Move ${config.field} down`}
                            className="rounded p-0.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700 disabled:opacity-30"
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>

                        {expanded && (
                          <div className="space-y-2 px-1 pb-2 pt-1.5">
                            <p className="text-[10px] text-slate-400">
                              Service field: <span className="font-mono">{config.field}</span>
                            </p>
                            <label className="block">
                              <span className="text-[10px] text-slate-500">Display label</span>
                              <input
                                value={config.label ?? ''}
                                onChange={(event) =>
                                  updateField(config.field, {
                                    label: event.target.value || undefined,
                                  })
                                }
                                placeholder={alias || config.field}
                                className="mt-0.5 w-full rounded border border-slate-200 px-1.5 py-1 text-[11px] text-slate-800 outline-none focus:border-blue-300"
                              />
                            </label>

                            <div className="grid grid-cols-2 gap-2">
                              <label className="block">
                                <span className="text-[10px] text-slate-500">Decimals</span>
                                <select
                                  value={config.format?.decimals ?? ''}
                                  onChange={(event) =>
                                    updateFormat(config.field, {
                                      decimals:
                                        event.target.value === ''
                                          ? undefined
                                          : Number(event.target.value),
                                    })
                                  }
                                  className="mt-0.5 w-full rounded border border-slate-200 px-1 py-1 text-[11px] text-slate-800 outline-none focus:border-blue-300"
                                >
                                  <option value="">Auto</option>
                                  <option value="0">0</option>
                                  <option value="1">1</option>
                                  <option value="2">2</option>
                                  <option value="3">3</option>
                                </select>
                              </label>
                              <label className="block">
                                <span className="text-[10px] text-slate-500">Date</span>
                                <select
                                  value={config.format?.dateFormat ?? ''}
                                  onChange={(event) =>
                                    updateFormat(config.field, {
                                      dateFormat:
                                        event.target.value === ''
                                          ? undefined
                                          : (event.target.value as 'date' | 'datetime'),
                                    })
                                  }
                                  className="mt-0.5 w-full rounded border border-slate-200 px-1 py-1 text-[11px] text-slate-800 outline-none focus:border-blue-300"
                                >
                                  <option value="">Not a date</option>
                                  <option value="date">Date only</option>
                                  <option value="datetime">Date &amp; time</option>
                                </select>
                              </label>
                              <label className="block">
                                <span className="text-[10px] text-slate-500">Prefix</span>
                                <input
                                  value={config.format?.prefix ?? ''}
                                  onChange={(event) =>
                                    updateFormat(config.field, { prefix: event.target.value })
                                  }
                                  placeholder="₹"
                                  className="mt-0.5 w-full rounded border border-slate-200 px-1.5 py-1 text-[11px] text-slate-800 outline-none focus:border-blue-300"
                                />
                              </label>
                              <label className="block">
                                <span className="text-[10px] text-slate-500">Suffix</span>
                                <input
                                  value={config.format?.suffix ?? ''}
                                  onChange={(event) =>
                                    updateFormat(config.field, { suffix: event.target.value })
                                  }
                                  placeholder=" mm"
                                  className="mt-0.5 w-full rounded border border-slate-200 px-1.5 py-1 text-[11px] text-slate-800 outline-none focus:border-blue-300"
                                />
                              </label>
                            </div>

                            <ToggleField
                              label="Group thousands"
                              checked={config.format?.thousands === true}
                              onChange={(thousands) =>
                                updateFormat(config.field, { thousands: thousands || undefined })
                              }
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {filtering && (
                  <p className="mt-1 text-[10px] text-slate-400">
                    Clear the search to reorder fields.
                  </p>
                )}
                </>
              )}

              {block.fields.length > 0 && (
                <div className="mt-2 space-y-2">
                  <ToggleField
                    label="Include new fields automatically"
                    checked={block.includeUnlisted}
                    onChange={(includeUnlisted) => {
                      setDraft((current) => ({
                        ...current,
                        blocks: [{ ...popupFieldsBlock(current), includeUnlisted }],
                      }));
                      setDirty(true);
                    }}
                  />
                  <p className="mt-1 text-[10px] leading-snug text-slate-400">
                    Off keeps the pop-up to exactly the fields above. On also shows columns added to
                    the service after this pop-up was set up.
                  </p>

                  <ToggleField
                    label="Show fields with no value"
                    checked={block.showEmpty}
                    onChange={(showEmpty) => {
                      setDraft((current) => ({
                        ...current,
                        blocks: [{ ...popupFieldsBlock(current), showEmpty }],
                      }));
                      setDirty(true);
                    }}
                  />
                  <p className="mt-1 text-[10px] leading-snug text-slate-400">
                    Off hides a row when the feature has nothing in that field. On keeps the row and
                    marks it empty, so the pop-up reads the same way for every feature.
                  </p>
                </div>
              )}
            </PropertySection>

            {/* ── PREVIEW ── */}
            <PropertySection title="Preview" collapsible>
              {Object.keys(sample).length === 0 ? (
                <p className="py-2 text-[11px] leading-snug text-slate-400">
                  {loadingSchema
                    ? 'Loading a feature to preview…'
                    : 'No feature could be read from this layer, so the preview is empty. The settings above still apply on the map.'}
                </p>
              ) : !draft.enabled ? (
                <p className="py-2 text-[11px] leading-snug text-slate-400">
                  Pop-up is off — clicking this layer opens nothing.
                </p>
              ) : (
                <div className="pointer-events-none rounded-lg bg-slate-100 p-2">
                  <FeatureAttributePopup
                    title={preview.title}
                    fields={preview.rows.map((row) => ({ field: row.label, value: row.value }))}
                    metadata={preview.metadata}
                    size="compact"
                    maxTableHeight={160}
                  />
                </div>
              )}
              <p className="mt-1.5 text-[10px] leading-snug text-slate-400">
                Rendered from a real feature in this layer, through the same code the map uses.
              </p>
            </PropertySection>
          </>
        )}
      </div>

      <footer className="flex flex-shrink-0 items-center justify-between gap-2 border-t border-slate-200 px-4 py-3">
        <button
          type="button"
          onClick={reset}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
        >
          Reset
        </button>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={cancel}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={apply}
            disabled={!dirty}
            className="rounded-lg bg-[#0F2942] px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
          >
            Apply
          </button>
        </div>
      </footer>
    </aside>
  );
}
